import { type Security, safeAbsurd } from '@ipfabric/utils';
import classNames from 'classnames';
import type { FC, RefObject } from 'react';

import { horizontalEllipsis } from '@ipfabric/frontend-shared-assets/utils/characters';
import { ButtonAsA } from 'components/Button/ButtonAsA';
import type { SecuritySchemaDispatch } from 'components/SecuritySchema/model/SecuritySchemaModel';
import { navigateToRuleChain } from 'components/SecuritySchema/model/commands/navigateToRuleChain';
import { pipe } from 'fp-ts/lib/function';

interface Props {
  action: Security.Action;
  dispatch: SecuritySchemaDispatch;
  securityType: Security.SecurityType;
  treeScrollContainerRef: RefObject<HTMLDivElement>;
  truncateActionNameTo?: number;
}

const baseLabelClasses = 'badge badge-inline text-uppercase';

export const getActionName = (action: Security.Action): string =>
  action.original ?? action.type;

export const SecurityActionLabel: FC<Props> = (props) => {
  const actionName = pipe(props.action, getActionName, (name) =>
    name.length > (props.truncateActionNameTo ?? Number.POSITIVE_INFINITY)
      ? `${name.substr(0, props.truncateActionNameTo)}${horizontalEllipsis}`
      : name,
  );
  if (props.action.type === 'exec') {
    const { chainName } = props.action;
    return (
      <>
        <span className={`${baseLabelClasses} badge-blue`}>{actionName}</span>
        &nbsp;
        <ButtonAsA
          onClick={() => {
            props.dispatch(navigateToRuleChain, {
              chainName,
              securityType: props.securityType,
              treeScrollContainer: props.treeScrollContainerRef.current,
            });
          }}
        >
          {chainName}
        </ButtonAsA>
      </>
    );
  }
  return (
    <span
      className={classNames(
        baseLabelClasses,
        (() => {
          switch (props.action.type) {
            case 'allow':
              return 'badge-success';
            case 'continue':
              return 'badge-light';
            case 'deny':
              return 'badge-danger';
            case 'return':
              return 'badge-dark';
            default:
              safeAbsurd(props.action);
              return 'badge-warning';
          }
        })(),
      )}
    >
      {actionName}
    </span>
  );
};
